/* My Jobs dashboard (Performance Analytics ▸ My Dashboards) — the login user's open
 * jobs: where each stands on the 7 steps, what to do next, and Resume to the form
 * holding that work. Design reference: "Home dashboard" prototype, 2026-11-02. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { useAuthStore } from '../../../auth/store';
import { fmtLocalDate } from '../../../utils/datetime';
import {
  displayStatus, loadMyJobs, matchesSearch, openableTarget, resumeJob, sampleCountText, startNewJob,
  type MyJobRow, type MyJobsDashboard,
} from './myJobs';
import './myJobs.css';

const STEP_COUNT = 7;

/** The row picked last, so coming back from a form lands on the test the user left. */
let lastRowId: string | null = null;

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  return hour < 18 ? 'Good afternoon' : 'Good evening';
}

function StepDots({ current }: { current: number }) {
  return (
    <span className="mj-dots" aria-hidden="true">
      {Array.from({ length: STEP_COUNT }, (_, i) => (
        <span key={i} className={`mj-dot${i < current ? ' done' : i === current ? ' current' : ''}`} />
      ))}
    </span>
  );
}

const stepLine = (j: MyJobRow) => `Step ${j.stepIndex + 1} of ${STEP_COUNT} · ${j.stepName}`;

export function MyJobsPage({ canSee }: { canSee?: (id: string, label?: string) => boolean }) {
  const notify = useNotify();
  const userName = useAuthStore((s) => s.user?.name);
  const [data, setData] = useState<MyJobsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(lastRowId);
  const [stepFilter, setStepFilter] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const started = performance.now();
    setLoading(true);
    try {
      const next = await loadMyJobs();
      if (seq === loadSeq.current) {
        setData(next);
        setLoadMs(Math.round(performance.now() - started));
      }
    } catch {
      // Logged by the API's exception middleware; the shell's client reports auth failures.
      if (seq === loadSeq.current) notify.error('Could not load your jobs.');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const targetOf = useCallback((job: MyJobRow) => openableTarget(job, canSee), [canSee]);

  const jobs = useMemo(() => data?.jobs ?? [], [data]);
  const rows = useMemo(
    () => jobs.filter((j) => (stepFilter == null || j.stepIndex === stepFilter) && matchesSearch(j, search)),
    [jobs, stepFilter, search],
  );
  const selected = jobs.find((j) => j.rowId === selectedId) ?? jobs[0] ?? null;
  const maxSamples = Math.max(1, ...(data?.steps ?? []).map((s) => s.samples));

  const pick = (job: MyJobRow) => { lastRowId = job.rowId; setSelectedId(job.rowId); };
  const resume = useCallback((job: MyJobRow) => {
    const target = openableTarget(job, canSee);
    if (!target) return;
    lastRowId = job.rowId;
    resumeJob(job, target.page);
  }, [canSee]);

  const columns = useMemo<AppColumn<MyJobRow>[]>(() => [
    { field: 'jobId', header: 'Job ID', width: 100 },
    { field: 'test', header: 'Test', minWidth: 120, wrap: true },
    { field: 'method', header: 'Method', width: 105, wrap: true },
    { field: 'client', header: 'Client', minWidth: 110, wrap: true },
    { field: 'sampleCount', header: 'Samples', width: 70, align: 'right' },
    {
      field: 'stepIndex', header: 'Progress', width: 135, text: stepLine,
      render: (j) => <span className="mj-progress"><StepDots current={j.stepIndex} /><span>{stepLine(j)}</span></span>,
    },
    { field: 'status', header: 'Status', type: 'status', width: 85, text: (j) => displayStatus(j, !!targetOf(j)) },
    { field: 'nextTitle', header: 'Next step', minWidth: 115, wrap: true },
    { field: 'qcBatchIds', header: 'QC Batch', width: 125, wrap: true, text: (j) => j.qcBatchIds.join(', ') },
    { field: 'dueDate', header: 'Due Date', type: 'date', width: 90 },
    {
      field: 'resume', header: '', width: 80, sortable: false, filterable: false, noExport: true,
      render: (j) => (
        <button type="button" className="ca-btn" disabled={!targetOf(j)}
          title={targetOf(j) ? `Open ${targetOf(j)?.label}` : `This step is at ${j.targetLabel}, which you don't have access to.`}
          onClick={(e) => { e.stopPropagation(); resume(j); }}>Resume</button>
      ),
    },
  ], [targetOf, resume]);

  const firstName = userName?.split(' ')[0];
  const openJobs = data?.openJobs ?? 0;
  const selectedTarget = selected ? targetOf(selected) : null;

  return (
    // Read-only, so nothing here is an unsaved edit. The unsaved-changes tracker takes the pressable
    // Sample flow cards for edits once "Submit · Clear filter" looks to it like a form's Submit button.
    <div className="pa-root" data-unsaved-ignore="">
      <div className="pa-page-scroll mj-page">
        <FormTitle pageId="pa-lab-myjobs" loadMs={loadMs} loading={loading} />
        <div className="mj-head">
        <AppToolbar
          onNew={startNewJob}
          newLabel="New Job"
          onRefresh={() => void load()}
          refreshing={loading}
          exportData={{
            filename: 'My-Jobs',
            headers: ['Job ID', 'Test', 'Method', 'Client', 'Samples', 'Step', 'Status', 'Next step', 'QC Batch', 'Due Date'],
            rows: rows.map((j) => [
              j.jobId, j.test ?? '', j.method ?? '', j.client ?? '', String(j.sampleCount), stepLine(j), displayStatus(j, !!targetOf(j)),
              j.nextTitle, j.qcBatchIds.join(', '), j.dueDate ? fmtLocalDate(j.dueDate) : '',
            ]),
          }}
        />

        <div className="mj-greeting">
          <h1>{greeting()}{firstName ? `, ${firstName}` : ''}</h1>
          <span className="mj-muted">
            {fmtLocalDate(new Date().toISOString())} · {openJobs} open job{openJobs === 1 ? '' : 's'} · {sampleCountText(data?.samplesInProgress ?? 0)} in progress
          </span>
        </div>
        </div>

        {selected ? (
          <section className="mj-card" aria-label="Current job progress">
            <div className="mj-working">
              <div>
                <span className="mj-kicker">Working on</span>
                <span className="mj-job-id">{selected.jobId}</span>
                {selected.test && <span className="mj-job-test">{selected.test}{selected.method ? ` · ${selected.method}` : ''}</span>}
                <span className="mj-muted">{selected.client ?? 'No client'} · {sampleCountText(selected.sampleCount)}</span>
              </div>
              <span className="mj-step-line">{stepLine(selected)}</span>
            </div>

            <ol className="mj-stepper">
              {(data?.steps ?? []).map((s) => {
                const state = s.stepIndex < selected.stepIndex ? 'done' : s.stepIndex === selected.stepIndex ? 'current' : 'todo';
                return (
                  <li key={s.stepIndex} className={`mj-step ${state}`} aria-current={state === 'current' ? 'step' : undefined}>
                    <span className="mj-step-circle">
                      {state === 'done' ? <i className="ti ti-check" aria-label="Done" /> : s.stepIndex + 1}
                    </span>
                    <span className="mj-step-name">{s.stepName}</span>
                  </li>
                );
              })}
            </ol>

            <div className={`mj-banner${selected.missing.length > 0 ? ' mj-banner-alert' : ''}`}>
              <div className="mj-banner-text">
                <div className="mj-kicker">Your next step</div>
                <div className="mj-next-title">{selected.nextTitle}</div>
                <div className="mj-next-desc">{selected.nextDescription}</div>
                {selected.samplesAtStep < selected.sampleCount && (
                  <div className="mj-next-tests">{selected.samplesAtStep} of {sampleCountText(selected.sampleCount)} at {selected.stepName}</div>
                )}
                {selected.missing.length > 0 && (
                  <div className="mj-missing">
                    {selected.missing.map((m) => (
                      <span key={m} className="mj-missing-chip"><i className="ti ti-alert-circle" aria-hidden="true" /> Required: {m}</span>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" className="ca-btn ca-btn-solid" disabled={!selectedTarget}
                title={selectedTarget ? undefined : `This step is at ${selected.targetLabel}, which you don't have access to.`}
                onClick={() => resume(selected)}>
                {!selectedTarget ? `Waiting at ${selected.targetLabel}`
                  : selectedTarget.page === selected.targetPage ? selected.nextAction : `Open ${selectedTarget.label}`}
                {' '}<i className="ti ti-arrow-right" aria-hidden="true" />
              </button>
            </div>
          </section>
        ) : !loading && (
          <section className="mj-card mj-empty">You have no open jobs. Jobs you create appear here until their report is sent.</section>
        )}

        <section className="mj-card" aria-label="Sample flow">
          <div className="mj-section-head">
            <div>
              <h2>Sample flow</h2>
              <div className="mj-muted">Samples of your jobs at each step. Select a step to filter your jobs.</div>
            </div>
            <strong>{data?.samplesInProgress ?? 0} samples in progress</strong>
          </div>
          <div className="mj-flow">
            {(data?.steps ?? []).map((s) => (
              <button key={s.stepIndex} type="button" className={`mj-flow-card${stepFilter === s.stepIndex ? ' pressed' : ''}`}
                aria-pressed={stepFilter === s.stepIndex}
                onClick={() => setStepFilter((f) => (f === s.stepIndex ? null : s.stepIndex))}>
                <span className="mj-flow-name">{s.stepName}</span>
                <span className="mj-flow-count">{s.samples}</span>
                <span className="mj-muted">{s.jobs === 1 ? '1 job' : `${s.jobs} jobs`}</span>
                <span className="mj-flow-track"><span className="mj-flow-bar" style={{ width: `${Math.round((s.samples / maxSamples) * 100)}%` }} /></span>
                {selected?.stepIndex === s.stepIndex && <span className="mj-flow-here">You are here</span>}
              </button>
            ))}
            <div className="mj-flow-card mj-flow-delivered">
              <span className="mj-flow-name">Delivered</span>
              <span className="mj-flow-count">{data?.deliveredLast7Days ?? 0}</span>
              <span>reports, last 7 days</span>
            </div>
          </div>
        </section>

        <div className="mj-columns">
          <section className="mj-card mj-list" aria-label="My open jobs">
            <div className="mj-section-head">
              <div>
                <h2>My open jobs</h2>
                <div className="mj-muted">Each test on a job you created has its own row until its report is sent. Pick one to see its next step.</div>
              </div>
              {stepFilter != null && (
                <button type="button" className="ca-btn" onClick={() => setStepFilter(null)}>
                  {data?.steps[stepFilter]?.stepName} · Clear filter
                </button>
              )}
            </div>
            <GridSearch value={search} onChange={setSearch} placeholder="Search Job ID, test, QC batch or client…" />
            <AppDataGrid
              rows={rows}
              columns={columns}
              getRowId={(j) => j.rowId}
              initialSort={{ key: 'jobId', dir: 'desc' }}
              loading={loading}
              hideHeaderTools
              accentSelection
              selectedRowId={selected?.rowId ?? null}
              onRowClick={pick}
              onRowDoubleClick={resume}
              emptyText={stepFilter != null ? 'No open jobs at this step.' : 'No open jobs.'}
            />
          </section>

          <aside className="mj-rail">
            <div className="mj-card">
              <h2>Start a new job</h2>
              <div className="mj-muted">Register the job and its samples in Sample Registration.</div>
              <button type="button" className="ca-btn ca-btn-solid" onClick={startNewJob}>
                <i className="ti ti-plus" aria-hidden="true" /> New Job
              </button>
            </div>
            <div className="mj-card mj-tip">
              <h2>Tip</h2>
              <div>At Sample Log In, enter a value once, right-click the cell and choose Copy to All Cells to fill it for every sample.</div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
